import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type {
  AutoBidRule,
  AutoBidRuleBasisUnselected,
} from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'

/**
 * 自動入札ルール片側 (エリア × 買/売) の状態 (AC-APF-056a / 076)。
 *
 * - `saved`: 保存済みとして数える側 (`auto_bid_rules`)
 * - `basisUnselected`: 保存済みの行はあるが基準を未選択で開く側
 *   (`auto_bid_rules_basis_unselected`)。 未保存として数える
 * - `neverSaved`: 一度も保存されていない側
 */
export type AutoBidSideSlot =
  | { kind: 'saved'; rule: AutoBidRule }
  | { kind: 'basisUnselected'; side: AutoBidRuleBasisUnselected }
  | { kind: 'neverSaved' }

export const NEVER_SAVED: AutoBidSideSlot = { kind: 'neverSaved' }

export type AutoBidAreaSlots = { buy: AutoBidSideSlot; sell: AutoBidSideSlot }

export type AutoBidSlotsByArea = ReadonlyMap<AreaCode, AutoBidAreaSlots>

/** ルールが 1 件も届かない業務処理用。 参照が変わらないよう共有する。 */
export const NO_AUTO_BID_SLOTS: AutoBidSlotsByArea = new Map()

type BidSideKind = keyof AutoBidAreaSlots

function bidSideKind(side: Side): BidSideKind | undefined {
  switch (side) {
    case Side.BUY:
      return 'buy'
    case Side.SELL:
      return 'sell'
    default:
      return undefined
  }
}

/**
 * GetApprovalFlowScreen の `auto_bid_rules` (保存済み) と
 * `auto_bid_rules_basis_unselected` (基準未選択) をエリア × 買/売の slot に 1 回で
 * まとめる。 同じ側が両方に来たら、 保存済みと誤認しない `basisUnselected` を採る。
 */
export function groupAutoBidSlotsByArea(
  saved: readonly AutoBidRule[],
  basisUnselected: readonly AutoBidRuleBasisUnselected[],
): AutoBidSlotsByArea {
  const map = new Map<AreaCode, AutoBidAreaSlots>()
  const put = (area: AreaCode, side: Side, slot: AutoBidSideSlot): void => {
    const kind = bidSideKind(side)
    if (kind === undefined) return
    const entry = map.get(area) ?? { buy: NEVER_SAVED, sell: NEVER_SAVED }
    map.set(area, { ...entry, [kind]: slot })
  }
  for (const rule of saved) {
    put(rule.areaCode, rule.bidSide, { kind: 'saved', rule })
  }
  for (const side of basisUnselected) {
    put(side.areaCode, side.bidSide, { kind: 'basisUnselected', side })
  }
  return map
}

export function autoBidSideSlot(
  slots: AutoBidSlotsByArea,
  area: AreaCode,
  side: BidSideKind,
): AutoBidSideSlot {
  return slots.get(area)?.[side] ?? NEVER_SAVED
}

/** slot が保存済みならそのルール。 要約・スポットの編集の初期値に使う。 */
export function savedAutoBidRule(
  slot: AutoBidSideSlot,
): AutoBidRule | undefined {
  return slot.kind === 'saved' ? slot.rule : undefined
}

/**
 * 承認モード = 自動 のエリアで、 自動入札ルール (買い・売り) が保存済みとして
 * 揃っていないか (AC-01 / AC-APF-056a / 056b)。 片側でも `saved` 以外なら true
 * (SaveAutoBidRule は買い・売りをペアで保存するため、 片方だけの保存は成立しない)。
 */
export function isAutoBidRuleUnsaved(
  slots: AutoBidSlotsByArea,
  area: AreaCode,
): boolean {
  return (
    autoBidSideSlot(slots, area, 'buy').kind !== 'saved' ||
    autoBidSideSlot(slots, area, 'sell').kind !== 'saved'
  )
}

/**
 * 時間前入札のルールが保存済みか。 [[isAutoBidRuleUnsaved]] に加えて
 * **process_config が intraday であること**まで要求する。 spot のルールが混ざった
 * 状態で通すと、 時間前の要約が既定値に落ちて未保存を設定済みに見せる (鉄則2)。
 */
export function isAutoBidIntradayRuleSaved(
  slots: AutoBidSlotsByArea,
  area: AreaCode,
): boolean {
  const buy = savedAutoBidRule(autoBidSideSlot(slots, area, 'buy'))
  const sell = savedAutoBidRule(autoBidSideSlot(slots, area, 'sell'))
  return (
    buy?.processConfig.case === 'intraday' &&
    sell?.processConfig.case === 'intraday'
  )
}
